import Highcharts from 'highcharts';
import { ReportType } from '../../../models/end-client-reporting-package';
import { ChartPoint, ChartSeries, NOT_ASKED_LABEL } from '../report-aggregation';

/**
 * Layout maths shared by every chart block, plus the orientation metadata.
 *
 * Deliberately outside `report-block-registry.ts`: the registry imports the block components, so a
 * block importing the registry back would be a cycle, which the esbuild builder rejects outright
 * (see frontend/CLAUDE.md). This module imports only models and aggregation types, so everything
 * can depend on it.
 */

export type ChartOrientation = 'vertical' | 'horizontal';

// A full Record, not a Partial: an empty array explicitly means "no orientation", so a new type
// still has to say which it is rather than silently inheriting nothing. The FIRST entry is that
// type's default, so nothing can disagree about what "unset" means.
export const REPORT_BLOCK_ORIENTATIONS: Record<ReportType, readonly ChartOrientation[]> = {
  'bar-chart': ['horizontal', 'vertical'],
  'filter-comparison': ['horizontal', 'vertical'],
  'pie-chart': [],
  'donut-chart': [],
  'study-overview': [],
  'headline-stats': [],
  'kpi-score': [],
  'subgroup-panels': ['vertical', 'horizontal'],
  'action-list': [],
  'stacked-scale': [],
  'funnel': [],
  'segment-tiles': [],
  // A table has rows and columns, not an orientation -- transposing it is a different module, not a
  // setting on this one.
  'crosstab': [],
  // Left against right is the module. Turning it on its side would be a different chart entirely.
  'diverging-bars': [],
  // Tiers read down the page and every bar shares one baseline across them. Upright, the tier bands
  // would run across the bars rather than beside them.
  'tiered-ranking': [],
  // A list of quotations has no orientation to choose.
  'open-end-explorer': [],
  // Nor a cloud, which has no axis to turn.
  'word-cloud': [],
  // Nor a heatmap, which is the creative's own shape.
  'heatmap': [],
  // Nor writing, or a picture, which is drawn the shape it is.
  'text': [],
  'image': [],
  // Both axes are named measures, so swapping them is choosing different questions rather than
  // turning the chart on its side.
  'scatter-plot': [],
  // A matrix has rows and columns, not an orientation -- transposing it is a different module.
  'ownership-matrix': [],
  'stage-matrix': [],
  // Written panels, not a chart.
  'stage-explainer': [],
};

export const CHART_ORIENTATION_LABELS: Record<ChartOrientation, string> = {
  horizontal: 'Horizontal',
  vertical: 'Vertical',
};

export function defaultOrientationFor(reportType: ReportType): ChartOrientation {
  return REPORT_BLOCK_ORIENTATIONS[reportType][0] ?? 'horizontal';
}

export function isHorizontal(reportType: ReportType, orientation?: ChartOrientation): boolean {
  return (orientation ?? defaultOrientationFor(reportType)) === 'horizontal';
}

/**
 * Where the percentage axis stops, shared by every bar so their lengths stay comparable. A step of
 * headroom is added when a bar lands on the rounded max, or its end label has nowhere outside the
 * bar to sit; at 100 there is nowhere left to go, which `overflow: 'allow'` covers.
 */
export function axisMaxFor(series: ChartSeries[]): number {
  const peak = Math.max(0, ...series.flatMap(s => s.points.map(p => p.percent)));
  const rounded = Math.ceil(peak / 10) * 10;
  const ceiling = series.some(s => s.numeric) ? Number.POSITIVE_INFINITY : 100;
  return Math.min(ceiling, Math.max(10, peak >= rounded ? rounded + 10 : rounded));
}

/**
 * How a value reads on a chart. Percentages are the norm here, but a numeric battery's bars are
 * means -- a MaxDiff score of 55 is not 55% of anything -- so the suffix and the precision both
 * have to follow the data rather than being baked into every format string.
 */
export interface ValueFormat {
  /** A Highcharts format string for the number itself, e.g. "{y:.0f}%". */
  pointFormat: string;
  suffix: string;
  decimals: number;
}

export function valueFormat(numeric: boolean, unit?: 'score' | 'percent'): ValueFormat {
  // A score keeps a decimal because whole numbers hide the gaps between ranked items; a percentage
  // never needs one.
  if (numeric && unit !== 'percent') return { pointFormat: '{y:.1f}', suffix: '', decimals: 1 };
  return { pointFormat: '{y:.0f}%', suffix: '%', decimals: 0 };
}

/** The estimate used when nothing can be measured: half the font size per character. */
const AVERAGE_CHAR_RATIO = 0.5;

/**
 * One 2D context for the whole app -- creating a canvas per call is the expensive part, not the
 * measuring. `undefined` means "not tried yet"; `null` means there is none to be had (jsdom has no
 * canvas implementation, and every chart block runs under Jest), which falls back to the estimate.
 */
let measureCtx: CanvasRenderingContext2D | null | undefined;

function measureContext(): CanvasRenderingContext2D | null {
  if (measureCtx === undefined) {
    try {
      measureCtx = document.createElement('canvas').getContext('2d');
    } catch {
      measureCtx = null;
    }
  }
  return measureCtx;
}

/**
 * Bounded because the key includes the text: a deck with hundreds of long labels across several
 * rebuilds would otherwise grow it without limit. Clearing wholesale rather than evicting one entry
 * costs a few re-measures on a chart that is about to re-render anyway.
 */
const MAX_CACHED_WIDTHS = 5000;
const widthCache = new Map<string, number>();

/**
 * How wide `text` renders. Measured for real, because the character-count estimate this replaces
 * was wrong by a factor of two either way -- "Illinois" and "Wyoming" have the same length and very
 * different widths, and a label wider than its allowance is what makes a bar disappear.
 */
export function textWidth(text: string, fontSize: number, fontFamily: string): number {
  const font = `${fontSize}px ${fontFamily}`;
  const key = `${font}|${text}`;
  const cached = widthCache.get(key);
  if (cached !== undefined) return cached;

  const context = measureContext();
  let width: number;
  if (context) {
    context.font = font;
    width = context.measureText(text).width;
  } else {
    width = text.length * fontSize * AVERAGE_CHAR_RATIO;
  }

  if (widthCache.size >= MAX_CACHED_WIDTHS) widthCache.clear();
  widthCache.set(key, width);
  return width;
}

/** Everything measured before the page's webfont arrived was measured in the wrong font. */
export function clearTextMeasureCache(): void {
  widthCache.clear();
}

/**
 * Greedy word wrap at `maxWidth`, matching what Highcharts does to an axis label: it breaks on
 * spaces and nowhere else, so a single word longer than the allowance gets its own line and
 * overflows it. That overflow is the thing to plan for -- Highcharts sizes the axis gutter from
 * whatever the label actually measures, so an unbreakable word takes the space the bars needed.
 */
export function wrapLines(
  text: string, maxWidth: number, fontSize: number, fontFamily: string,
): string[] {
  // Split after hyphens as well as on spaces, because Highcharts' own TextBuilder does -- a rule we
  // have to match or it finds a break we did not reserve a line for.
  const words = text.replace(/([^\s^])-/g, '$1- ').split(/\s+/).filter(Boolean);
  if (!words.length) return [''];

  const lines: string[] = [];
  let line = '';

  const push = () => { if (line) lines.push(line.trimEnd()); line = ''; };

  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (line && textWidth(candidate, fontSize, fontFamily) > maxWidth) {
      push();
      line = word;
    } else {
      line = candidate;
    }

    // A line holding one word can still be too wide, and Highcharts would render it at that width
    // -- it never breaks inside a word. The axis gutter then grows to match, which on a mini panel
    // is what leaves no room for the bar at all. So break it here, and every line returned
    // genuinely fits. Only single-word lines can need this; anything with a space already wrapped.
    if (line.includes(' ')) continue;
    while (line.length > 1 && textWidth(line, fontSize, fontFamily) > maxWidth) {
      let head = line;
      while (head.length > 1 && textWidth(head, fontSize, fontFamily) > maxWidth) {
        head = head.slice(0, -1);
      }
      lines.push(head);
      line = line.slice(head.length);
    }
  }
  push();

  return lines.length ? lines : [''];
}

/**
 * The line height Highcharts will actually use: `SVGRenderer.fontMetrics` is `size + 3` below 24px.
 * Reserving anything else means the height we compute and the height it draws disagree.
 */
export function lineHeightFor(fontSize: number): number {
  return fontSize < 24 ? fontSize + 3 : Math.round(fontSize * 1.2);
}

/**
 * `text` cut to at most `maxLines` of `maxWidth`, with the last line ellipsised. Ours rather than
 * Highcharts' `textOverflow: 'ellipsis'`, which collapses a label to a *single* line -- we want the
 * label to use the lines it has been given and only then give up. Cutting mid-word is deliberate:
 * the alternative on an unbreakable word is to render nothing.
 */
export function clampLabel(
  text: string, maxWidth: number, maxLines: number, fontSize: number, fontFamily: string,
): string {
  const lines = wrapLines(text, maxWidth, fontSize, fontFamily);
  const kept = lines.slice(0, Math.max(1, maxLines));
  const fits = kept.length === lines.length
    && kept.every(line => textWidth(line, fontSize, fontFamily) <= maxWidth);
  if (fits) return text;

  let last = kept[kept.length - 1];
  const rest = lines.slice(kept.length).join(' ');
  if (rest) last = `${last} ${rest}`;

  while (last && textWidth(`${last}…`, fontSize, fontFamily) > maxWidth) {
    last = last.slice(0, -1).trimEnd();
  }

  kept[kept.length - 1] = `${last}…`;
  return kept.join(' ');
}

/**
 * The share of a horizontal chart's width its category labels may take. Without a ceiling the
 * gutter is a flat 300px whatever the block is sized at, so a half-width block spends three
 * quarters of itself on labels -- and a label too long for the lines reserved for it runs into the
 * category below rather than being cut. Same rule small multiples already applies per panel.
 *
 * 0.46 is the prototype's own split, read off `.row .lab { width: 46% }` in its styles.css: just
 * under half, so the bars always have the longer side of the row and the chart still reads as a
 * chart rather than as a list with bars after it.
 */
const MAX_LABEL_SHARE = 0.46;

/**
 * The prototype's bar geometry, read off `.row` in its styles.css. Shared by every module that
 * draws a horizontal bar so they come out identical card to card, which is the whole point: two
 * bar charts on one page that disagree about thickness or where the labels stop read as two
 * different charts rather than two readings.
 *
 * Thickness is a flat `pointWidth`, not a share of the slot. Padding gives a *proportion*, so the
 * same bar comes out thinner on a chart with more categories.
 */
export const BAR_THICKNESS_PX = 15;
export const BAR_GAP_PX = 7;
/**
 * The bar corner. An option, not CSS -- and the two facts behind that are both in the Highcharts
 * source rather than its docs, so they are recorded here.
 *
 * 1. A column or bar point is **always** drawn as a `roundedRect` path (highcharts.src.js:41713,
 *    `point.shapeType = ... || 'roundedRect'`), never as a `<rect>`. So the CSS `rx`/`ry`
 *    properties can never round a bar, and the app-wide `themes/highcharts.css` rule that sets
 *    `rx:8` on `.highcharts-point` has never had any effect on one.
 * 2. The rounding is skipped entirely when the option is falsy -- `if (this.options.borderRadius)`
 *    at highcharts.src.js:45481. So `borderRadius: 0` does not mean "square corners drawn by
 *    something else", it means the radius is never computed at all.
 *
 * `where: 'all'` is the part that matters: left unset it defaults to `'end'` (:45516), which
 * inflates the radius box past the axis so only the far corners round -- right for a column
 * growing off a baseline, wrong for the prototype's `.row .bar > span`, a rounded rectangle in its
 * own right. `scope: 'stack'` rounds a stacked bar as one shape: its two ends are rounded and the
 * joins between its segments stay square, since a corner at every join reads as a row of separate
 * pills. An unstacked bar is its own stack, so it is rounded at both ends as before.
 */
export const BAR_BORDER_RADIUS = {
  radius: 3,
  scope: 'stack',
  where: 'all',
} as Highcharts.BorderRadiusOptionsObject;

const easeOutCubic = (pos: number): number => 1 - Math.pow(1 - pos, 3);

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/**
 * One motion for every chart in the deck: an eased draw-in, and a shorter move when the figures
 * change (a filter). `animateUpdates: false` is for a rebuild that only corrects the layout -- the
 * real width measured, the webfont arrived -- which should land at once rather than slide the bars
 * and their tracks from where an estimate put them.
 */
export function withDeckMotion(options: Highcharts.Options, animateUpdates = true): Highcharts.Options {
  const reduced = prefersReducedMotion();
  const entry: Highcharts.AnimationOptionsObject | false = reduced ? false : { duration: 700, easing: easeOutCubic };
  const update: Highcharts.AnimationOptionsObject | false =
    reduced || !animateUpdates ? false : { duration: 450, easing: easeOutCubic };
  return {
    ...options,
    chart: { ...options.chart, animation: update },
    plotOptions: {
      ...options.plotOptions,
      series: { ...options.plotOptions?.series, animation: entry },
    },
  };
}

/** `.row .lab { width: 46% }`: the labels take this share of the card, the bars take the rest. */
export const BAR_LABEL_SHARE = 0.46;
/** `.lab { padding-right: 8px }`: clear air between the longest answer and its bar. */
export const BAR_LABEL_GUTTER_PX = 8;

/**
 * The label column, in pixels, for a chart of this width. Pinned as a left margin by the caller
 * rather than left to Highcharts, which sizes the gutter to whichever label happens to be longest
 * -- so two charts on the same page started their bars in different places.
 */
export function barLabelColumn(plotWidth: number): number {
  return Math.max(80, Math.floor(plotWidth * BAR_LABEL_SHARE));
}

/**
 * Category-label placement, answered for **both** orientations rather than only the one that needs
 * something set.
 *
 * Horizontal ranges the labels left from the card's edge: Highcharts anchors an inverted chart's
 * labels at the axis and ranges them right, so the offset pulls the anchor back across the whole
 * column to its start. Vertical wants neither -- and has to say so, for the reason `categoryAxis`
 * records below. highcharts-angular updates with `chart.update(options, true, false)`, which
 * *merges*, so a key set while the chart was horizontal and simply left out afterwards keeps its
 * horizontal value. Turning a ranked bar vertical left its answers ranged left and shifted a whole
 * label column off the side of the chart, which is what "vertical is broken" looked like.
 */
export function categoryLabelPlacement(
  horizontal: boolean, labelColumn: number,
): Highcharts.XAxisLabelsOptions {
  return {
    align: horizontal ? 'left' : 'center',
    x: horizontal ? -labelColumn : 0,
  };
}

/**
 * A bar only takes the flat thickness if its slot can hold it. Past that the slot is being squeezed
 * by a height cap, and a fixed width would have the bars overlap rather than thin out.
 */
export function barPointWidth(rowHeight: number, bars = 1): Record<string, number> {
  return rowHeight >= bars * BAR_THICKNESS_PX + 2 ? { pointWidth: BAR_THICKNESS_PX } : {};
}

/**
 * How wide a horizontal chart's category labels may wrap, given the width the plot actually has.
 * `cap` is the block's own preferred maximum; this only ever narrows it.
 */
export function horizontalLabelWidth(plotWidth: number, cap: number): number {
  return Math.max(80, Math.min(cap, Math.floor(plotWidth * MAX_LABEL_SHARE)));
}

/**
 * Every category cut to the lines its layout reserved room for, keyed by the full text.
 *
 * The map is keyed by the original because that stays the chart's category: the tooltip header and
 * the aggregation both go on reading every word, and only what is *drawn* is shortened.
 */
export function clampedCategories(
  categories: readonly string[], fit: LabelFit, labelSize: number, fontFamily: string,
): Map<string, string> {
  return new Map(categories.map(category => [
    category, clampLabel(category, fit.wrapWidth, fit.lines, labelSize, fontFamily),
  ]));
}

/**
 * The axis formatter that draws those. Without one the axis renders the raw category, the style's
 * wrap width caps its width and nothing caps its height -- which is the overlap, not a near miss:
 * Highcharts will happily draw a seven-line label into a slot sized for four.
 */
export function categoryLabelFormatter(
  labels: Map<string, string>, italiciseNotAsked = false,
): Highcharts.AxisLabelsFormatterCallbackFunction {
  return function (this: Highcharts.AxisLabelsFormatterContextObject): string {
    const value = String(this.value);
    const shown = labels.get(value) ?? value;
    return italiciseNotAsked && value === NOT_ASKED_LABEL ? `<em>${shown}</em>` : shown;
  };
}

export interface LabelFit {
  /** Pixel width each category label may occupy before it wraps. */
  wrapWidth: number;
  /** Lines the longest label is expected to need. Callers reserve height from this. */
  lines: number;
  /**
   * The widest line any label actually renders, measured -- only set when `fontFamily` was given.
   * Larger than `wrapWidth` when a single word could not be broken, which is exactly when a chart
   * needs to be told how much gutter it may take.
   */
  widest?: number;
}

/**
 * How much room a category label gets, and how many lines it will take.
 *
 * Horizontal charts label down the left, where a fixed allowance works. Vertical ones label along
 * the bottom, where each gets only its own column's share of the width -- honoured strictly, minus
 * a gutter, or a long answer reaches into its neighbour. `plotWidth` must therefore be measured
 * rather than assumed; estimating it chopped labels to "$40,..." whenever it came in low.
 */
export function labelFitFor(options: {
  plotWidth: number;
  slots: number;
  categories: string[];
  labelSize: number;
  horizontal: boolean;
  horizontalWidth: number;
  /** Pass the family the chart will render with to measure for real instead of estimating. */
  fontFamily?: string;
  /** Ceiling on wrapped lines; 4 unless the caller has a height budget that says otherwise. */
  maxLines?: number;
}): LabelFit {
  const {
    plotWidth, slots, categories, labelSize, horizontal, horizontalWidth, fontFamily,
  } = options;
  const maxLines = Math.max(1, options.maxLines ?? 4);

  // A floor against zero/negative width only -- not an attempt at a readable minimum.
  const slotWidth = Math.max(24, Math.floor(plotWidth / Math.max(1, slots)) - 8);
  const wrapWidth = horizontal ? horizontalWidth : slotWidth;

  if (!fontFamily) {
    const charsPerLine = Math.max(4, Math.floor(wrapWidth / (labelSize / 2)));
    const longest = Math.max(0, ...categories.map(c => c.length));
    return { wrapWidth, lines: Math.min(maxLines, Math.max(1, Math.ceil(longest / charsPerLine))) };
  }

  let lines = 1;
  let widest = 0;
  for (const category of categories) {
    const wrapped = wrapLines(category, wrapWidth, labelSize, fontFamily);
    lines = Math.max(lines, wrapped.length);
    for (const line of wrapped) widest = Math.max(widest, textWidth(line, labelSize, fontFamily));
  }

  return { wrapWidth, lines: Math.min(maxLines, lines), widest: Math.ceil(widest) };
}

/**
 * The category axis with its labels switched off. The gutter they occupied goes back to the plot,
 * which is the whole point of the setting -- on a horizontal bar chart the labels can be half the
 * width. Nothing is lost: the category is still the tooltip's header.
 */
export function categoryAxis(axis: Highcharts.XAxisOptions, shown: boolean): Highcharts.XAxisOptions {
  return {
    ...axis,
    // Every key set both ways on purpose. highcharts-angular applies a new options object with
    // `chart.update(options, true, false)`, which *merges* -- so a key present while the labels
    // were hidden and absent once they are shown keeps its hidden value. Swapping in a different
    // axis object was exactly that: hiding worked, and showing again did nothing at all.
    labels: { ...axis.labels, enabled: shown },
    lineWidth: shown ? 1 : 0,
    tickLength: shown ? 10 : 0,
  };
}

/** The category-axis label style. No textOverflow, so nothing is ever ellipsised. */
export function categoryLabelStyle(fit: LabelFit, labelSize: number): Highcharts.CSSObject {
  // The deck's ink, named here because Highcharts writes its own grey onto every label as an inline
  // style, which no stylesheet can reach -- so this is the one place the bar, stacked, subgroup and
  // dynamic distribution labels can take the report's dark text from.
  return {
    fontSize: `${labelSize}px`,
    whiteSpace: 'normal',
    width: fit.wrapWidth,
    color: 'var(--ecr-ink, #1b2b33)',
  };
}

/**
 * The percentage printed at the end of each bar. `crop: false` / `overflow: 'allow'` matter because
 * a bar reaching axisMax puts its label in the margin, where the default is to drop it silently --
 * and with the axis hidden, nothing else carries the value.
 */
export function endLabelDataLabels(fontSize = 11, format = '{y:.0f}%'): Highcharts.DataLabelsOptions {
  return {
    enabled: true,
    format,
    inside: false,
    crop: false,
    overflow: 'allow',
    // The prototype's `.row .val`: a supporting figure beside the bar, so muted and at normal
    // weight. The bar is the reading; the number annotates it.
    color: 'var(--ecr-mut, #5a6b73)',
    style: { fontSize: `${fontSize}px`, fontWeight: '400', textOutline: 'none' },
  };
}

/**
 * Below this percentage a bar has no room to print its own figure, so the figure goes outside it.
 *
 * A share rather than a pixel width: every bar on a chart is drawn against one axis maximum, so a
 * percentage converts to the same fraction of the plot on every row, and one threshold decides it
 * for the whole chart instead of measuring bar by bar.
 */
export const INSIDE_LABEL_MIN_PERCENT = 4;

/**
 * The figure at the end of a bar, moved inside it once the bar is long enough to hold it.
 *
 * Returns nothing below the threshold, which leaves the series-level outside label in force.
 * `insideInk` is picked against the bar's own fill by the caller, since an inside label is printed
 * on the bar and a light palette would otherwise leave it unreadable.
 */
export function endLabelFor(percent: number, insideInk: string)
  : Highcharts.DataLabelsOptions {
  // Always an object, never undefined. A point whose dataLabels appear and disappear between
  // renders changes shape, and on a redraw -- a resize, a zoom -- Highcharts reconciles the new
  // label without destroying the old one, so the figure is drawn twice.
  if (percent < INSIDE_LABEL_MIN_PERCENT) return { inside: false };
  // Bold again here, unlike the outside figure: printed on the fill it needs the weight to hold.
  return { inside: true, align: 'right', color: insideInk, style: { fontWeight: '600' } };
}

/** The hidden percentage axis: `max` still fixes bar lengths, but the printed number is what reads. */
/** Room over a column chart's tallest column for the figure printed above it. */
const COLUMN_LABEL_ROOM_PX = 20;

/**
 * A column chart's value axis: up to its tallest column and no further, with just the room above it
 * that the column's own figure needs.
 *
 * Not rounded up to the next ten, the way a bar chart's axis is. A bar is drawn against a track that
 * runs to the end of the axis, and a round figure gives the tracks a clean end; a column has no
 * track, so on a column chart the rounding was only empty space over the tallest column -- a 41%
 * peak left the top fifth of the plot bare.
 */
export function columnValueAxis(compact: boolean): Highcharts.YAxisOptions {
  const plot = compact ? COLUMN_PLOT_COMPACT_PX : COLUMN_PLOT_PX;
  return {
    title: { text: undefined },
    min: 0,
    // Cleared rather than left out: an options update merges, and a max from a bar chart's axis
    // would otherwise survive the chart being turned upright.
    max: null,
    // Something to draw against when every column is zero.
    softMax: 1,
    startOnTick: false,
    endOnTick: false,
    maxPadding: COLUMN_LABEL_ROOM_PX / plot,
    labels: { enabled: false },
    gridLineWidth: 0,
    lineWidth: 0,
    tickLength: 0,
  };
}

export function hiddenPercentAxis(axisMax: number): Highcharts.YAxisOptions {
  return {
    title: { text: undefined },
    max: axisMax,
    labels: { enabled: false },
    gridLineWidth: 0,
    lineWidth: 0,
    tickLength: 0,
  };
}

/** Shared by pie and donut: the answer text lives here, so it wraps and carries the NOT_ASKED italic. */
/** Widest a legend item may be before it is ellipsised. */
export const LEGEND_ITEM_WIDTH = 220;
// Highcharts' own numbers, so the height reserved here is the height it actually takes:
// symbolWidth 16 + symbolPadding 5 + itemDistance 20 beside each item (Core/Defaults.js), and a row
// of itemMarginTop 2 + itemHeight 16 + itemMarginBottom 2. The first row also carries
// initialItemY (padding - 5) and the box padding, and adjustMargins then adds legend.margin 12 plus
// the chart's own bottom spacing 12 (Legend.js:611-615, :742-745).
const LEGEND_ITEM_EXTRA = 41;
const LEGEND_FIRST_ROW_PX = 31;
const LEGEND_ROW_PX = 20;
const LEGEND_CHROME_PX = 24;

/** What a legend of `rows` rows takes out of the plot. */
export function legendHeightFor(rows: number): number {
  return rows < 1 ? 0 : LEGEND_FIRST_ROW_PX + (rows - 1) * LEGEND_ROW_PX + LEGEND_CHROME_PX;
}

/**
 * The legend for a chart whose series are whole questions. Each item is held to one ellipsised line
 * because a series name here is the datamap's question text, which on a real survey runs to a few
 * hundred characters -- left to wrap, three of them fill more of the chart than the bars do. The
 * full text stays in the tooltip.
 */
export function seriesLegend(): Highcharts.LegendOptions {
  return {
    enabled: true,
    align: 'center',
    verticalAlign: 'bottom',
    itemStyle: {
      // The prototype's legend size; see --ecr-font-xs in themes/end-client-reporting.scss.
      fontSize: '11px',
      fontWeight: '400',
      width: LEGEND_ITEM_WIDTH,
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
    },
  };
}

/**
 * How many rows that legend will take, so a caller can reserve the height. Highcharts lays the items
 * out left to right and wraps them, and takes what it needs out of the plot -- which, unreserved,
 * comes off the bars.
 */
export function legendRowsFor(names: readonly string[], plotWidth: number, fontFamily: string): number {
  if (names.length < 2) return 0;

  let rows = 1;
  let used = 0;

  for (const name of names) {
    const width = Math.min(LEGEND_ITEM_WIDTH, textWidth(name, 13, fontFamily)) + LEGEND_ITEM_EXTRA;
    // Highcharts adds the box padding to the legend's width *after* testing for a break, so a row
    // that exactly fills the plot overflows it. Break a little early.
    if (used > 0 && used + width > plotWidth - 8) {
      rows += 1;
      used = width;
    } else {
      used += width;
    }
  }

  return rows;
}

/**
 * The ring's diameter, and with it where its centre goes. Flat px rather than a share of the chart:
 * the ring is a glyph beside the key, not the thing being read, so it should not swell or shrink
 * with the number of answers. Centring it at half its own width pins it to the left edge of the
 * plot -- left to itself Highcharts centres it in whatever the legend leaves, which moves the ring
 * every time an answer list gets longer.
 */
export const SLICE_SIZE_PX = 110;
export const SLICE_CENTRE: [number, string] = [SLICE_SIZE_PX / 2, '50%'];
/** Clear air between the ring and the key that reads it. */
const SLICE_LEGEND_GAP = 32;
/**
 * How wide a key entry may run before it is cut.
 *
 * A floating legend claims no space, so nothing stops a long answer running off the card -- the
 * width has to be stated. Fixed rather than measured because these two blocks do not extend
 * ChartBlockBase and have no measured width to divide up; these are the widest a key entry can be
 * on a full-width and a half-width card without reaching the edge.
 */
const SLICE_LABEL_WIDTH = 240;
const SLICE_LABEL_WIDTH_COMPACT = 150;

export function sliceLegend(compact = false): Highcharts.LegendOptions {
  const labelWidth = compact ? SLICE_LABEL_WIDTH_COMPACT : SLICE_LABEL_WIDTH;
  return {
    enabled: true,
    // Beside the ring, not under it, and one answer per line: the prototype reads as a key you go
    // down rather than a strip you scan across, and the figures line up as a column because of it.
    layout: 'vertical',
    // Beside the ring, not at the far edge of the card. Floating so it claims no space of its own:
    // a legend that reserves width moves the plot, and the ring is pinned to a fixed centre, so the
    // two would fight -- the ring would sit left of a gap with the key stranded across the card.
    floating: true,
    align: 'left',
    x: SLICE_SIZE_PX + SLICE_LEGEND_GAP,
    verticalAlign: 'middle',
    itemMarginBottom: 3,
    // Highcharts' own symbol is switched off and the swatch drawn in the label instead. Its
    // symbolRadius rounds a square only for some series types, and a pie's legend symbol ignored
    // it -- drawing it here is one rounded square on every chart, and it also puts the whole entry
    // inside one element, so nothing of ours sits against a boundary Highcharts can clip.
    symbolWidth: 0,
    symbolHeight: 0,
    symbolPadding: 0,
    useHTML: true,
    itemStyle: { fontSize: '11px', fontWeight: '400', color: 'var(--ecr-ink, #1b2b33)' },
    // The percentage belongs to the answer, so it is read here rather than crammed onto a slice
    // that may be too thin to hold it. Bold because it is the figure; the answer text is the label.
    // Laid out rather than concatenated, so only the answer is cut. A single truncating run would
    // eat the figure first -- it is last in the string -- which is the one part of the entry that
    // cannot be guessed from what survives. The swatch and the figure are both fixed; only the
    // middle shrinks. One line each: a wrapped entry puts its figure on a line of its own and
    // breaks the column of numbers the key is read down.
    labelFormatter: function (this: Highcharts.Point): string {
      const name = this.name === NOT_ASKED_LABEL ? `<em>${this.name}</em>` : this.name;
      // inline-flex against a max-width, not a fixed width: the entry is only as wide as it needs
      // to be, so the figure sits against the end of the answer instead of being pushed to a
      // right-hand edge that a short answer never reaches.
      return `<span style="display:inline-flex;align-items:center;gap:6px;max-width:${labelWidth}px">`
        + '<i style="flex:0 0 auto;width:10px;height:10px;border-radius:2px;'
        + `background:${this.color as string}"></i>`
        + '<span style="flex:0 1 auto;min-width:0;overflow:hidden;text-overflow:ellipsis;'
        + `white-space:nowrap">${name}</span>`
        + `<b style="flex:0 0 auto;white-space:nowrap">${Math.round(this.y ?? 0)}%</b></span>`;
    },
  };
}

/**
 * Segment data for a pie or donut. Colour is keyed to the answer *option*, not the segment's
 * position, so an option keeps its colour across compare columns and across charts of the same
 * question. "Not asked" has optionIndex -1 and takes the faded track treatment.
 */
export function slicePoints(points: ChartPoint[], roles: string[]): Highcharts.PointOptionsObject[] {
  return points.map(point => {
    const index = Math.max(0, point.optionIndex);
    return {
      name: point.label,
      y: point.percent,
      custom: { count: point.value },
      colorIndex: index,
      color: roles[index] ?? roles[roles.length - 1],
      className: point.optionIndex < 0 ? 'chart-track' : undefined,
    };
  });
}

/**
 * Breathing room inside a chart's rounded white box, so axis labels do not sit against its edge.
 * Larger than Highcharts' default because the box now has a visible corner to crowd. The 12 either
 * side is what plotWidth() subtracts.
 */
export const CHART_SPACING: [number, number, number, number] = [12, 12, 12, 12];

/**
 * What a chart spends above and below its plot, given the spacing above.
 *
 * Matters because a horizontal chart's height is set from its rows: everything reserved beyond what
 * is actually used becomes plot height, and Highcharts divides plot height evenly across the
 * categories. Over-reserving does not leave a margin -- it silently widens the gap between every
 * bar, and the fewer the rows the further the slack has to spread.
 */
export const CHART_V_SPACING = CHART_SPACING[0] + CHART_SPACING[2];

/**
 * How tall an upright chart's plot is, however many answers it draws. The answers run across it, so
 * more of them make each column narrower rather than the chart taller -- sizing the height by the
 * answer count drew an eleven-point scale nearly 500px tall to hold columns that needed a third of
 * it. Compact is a panel sitting beside another.
 */
export const COLUMN_PLOT_PX = 170;
export const COLUMN_PLOT_COMPACT_PX = 130;

/**
 * As CHART_SPACING, but with nothing on the left: the ring is pinned to the plot's left edge, and
 * spacing there would inset it from the card while the heading above stayed flush.
 */
export const SLICE_SPACING: [number, number, number, number] =
  [CHART_SPACING[0], CHART_SPACING[1], CHART_SPACING[2], 0];

/**
 * The panels of a small-multiples grid. A panel's white box *is* the chart, so CSS padding around it
 * does nothing for the labels inside -- this is the only thing keeping a label off the corner.
 */
export const PANEL_SPACING: [number, number, number, number] = [12, 12, 12, 12];

/**
 * Margin reserved for an end label on a bar that fills the axis, where headroom ran out. The
 * default suits a full-size chart; `reserve` is for a mini panel, where 46px of a 150px panel is a
 * third of the space the bars were supposed to get.
 */
export function endLabelMargins(
  horizontal: boolean, reserve?: number,
): Pick<Highcharts.ChartOptions, 'marginRight' | 'marginTop'> {
  return {
    marginRight: horizontal ? reserve ?? 46 : undefined,
    marginTop: horizontal ? undefined : reserve ?? 26,
  };
}
